import React, { useMemo, useRef, useEffect, useState } from "react";
import { TbShare } from "react-icons/tb";
import { CategoryInterface } from "../../../../common/types/types";
import Search from "../../../../common/components/search/Search";
import BottomModal from "../../../../common/components/bottomModal/BottomModal";
import ShareModalContent from "../../../../common/components/share/components/shareModalContent/ShareModalContent";
import styles from "./CategoryList.module.scss";

interface CategoryListProps {
  categoryList: CategoryInterface[];
  selectedCategory?: number;
  onSelectCategory: (id: number | undefined) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  showCategories?: boolean;
}

export default function CategoryList({
  selectedCategory,
  onSelectCategory,
  categoryList,
  searchQuery,
  onSearchChange,
  onSearchClear,
  showCategories = true,
}: CategoryListProps) {
  const [isOverflow, setIsOverflow] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const allCategories = useMemo(
    () => [{ name: "Все", id: undefined }, ...categoryList],
    [categoryList],
  );

  const categoryName = useMemo(
    () => categoryList.find((c) => c.id === selectedCategory)?.name,
    [categoryList, selectedCategory],
  );

  const shareText = searchQuery
    ? `Поиск: ${searchQuery}`
    : selectedCategory
      ? `Категория: ${categoryName}`
      : "Каталог";

  useEffect(() => {
    const checkOverflow = () => {
      if (listRef.current) {
        const hasOverflow =
          listRef.current.scrollWidth > listRef.current.clientWidth;
        setIsOverflow(hasOverflow);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [allCategories]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!selectedCategory || !listRef.current) return;

    const activeButton = listRef.current.querySelector(
      `.${styles.active}`,
    ) as HTMLElement | null;

    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedCategory]);

  if (!categoryList.length) return null;

  return (
    <div
      className={`${styles.container} ${scrolled ? styles.scrolled : ""} ${!showCategories ? styles.compact : ""}`}
    >
      <div className={styles.search}>
        <div className={styles.searchMobile}>
          <Search
            onChange={onSearchChange}
            clear={onSearchClear}
            value={searchQuery}
          />
          <button
            className={styles.shareButton}
            onClick={() => setShareModal(true)}
            aria-label="Поделиться"
          >
            <TbShare size={20} />
          </button>
        </div>
      </div>

      {showCategories && (
        <div
          ref={listRef}
          className={`${styles.categoriesList} ${isOverflow ? styles.overflow : ""}`}
        >
          {allCategories.map((category) => (
            <button
              key={category.id ?? "all"}
              className={`${styles.categoryButton} ${
                selectedCategory === category.id ? styles.active : ""
              }`}
              onClick={() => onSelectCategory(category.id)}
            >
              <span className={styles.categoryText}>{category.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className={styles.search}>
        <div className={styles.searchDesktop}>
          <Search
            onChange={onSearchChange}
            clear={onSearchClear}
            value={searchQuery}
          />
          <button
            className={styles.shareButton}
            onClick={() => setShareModal(true)}
            aria-label="Поделиться"
          >
            <TbShare size={20} />
          </button>
        </div>
      </div>

      <BottomModal isVisible={shareModal} onClose={() => setShareModal(false)}>
        <ShareModalContent
          url={window.location.href}
          text={shareText}
          onClose={() => setShareModal(false)}
        />
      </BottomModal>
    </div>
  );
}
